import type {
  CodeExecutionHookParams,
  CodeExecutionHookReturn,
} from "../useProvider";

import * as React from "react";

import {
  codeExecutionClient,
  codeExecutionOptions,
  codeExecutionRouter,
} from "@editor/trpc/codeExecution";

import {
  useCurrentStyleLibrary,
  useViewingVersionId,
} from "@/features/react-editor/store/SandboxStateStoreContext";
import { skipToken, useQuery } from "@tanstack/react-query";

export function useDaytona({
  processLogMessage,
  projectId,
  incrementRefreshEpoch,
  forceSkip,
}: CodeExecutionHookParams): CodeExecutionHookReturn {
  const viewingVersionId = useViewingVersionId();
  const currentStyleLibrary = useCurrentStyleLibrary();

  const { data } = useQuery(
    codeExecutionOptions.daytona.getPreviewUrl.queryOptions(
      projectId && !forceSkip
        ? {
            projectId,
            viewingVersionId: viewingVersionId ?? undefined,
            currentStyleLibrary: currentStyleLibrary ?? undefined,
          }
        : skipToken,
      {
        // NOTE (Bobby, 2025-09-19): Setting the stale time to 30 seconds so that if window focus is triggered multiple times in a short period (switching tabs back and forth)
        // we don't make multiple requests to the server. I think this time needs to be at least as long as the refetchInterval. This is basically telling react query the minimum
        // amount of time that needs to pass before we consider the data stale and make any new requests. This gives enough buffer to allow the first getPreviewUrl request to finish
        // so we never run into issues with race conditions around calling that multiple times.
        staleTime: 30000,
        refetchOnWindowFocus: true,
        // NOTE (Bobby, 2025-09-19): While the user's actively on the tab, we want to keep refetching and keeping the sandbox alive.
        refetchInterval: 30000,
        refetchIntervalInBackground: false,
      },
    ),
  );

  const { reset: resetLogSubscription } =
    codeExecutionRouter.daytona.streamLogs.useSubscription(
      projectId && !forceSkip ? { projectId } : skipToken,
      {
        onData: (data: string) => {
          processLogMessage(data);
        },
        onError: (error) => {
          setError(error);
        },
      },
    );

  const [error, setError] = React.useState<unknown | undefined>(undefined);

  // NOTE (Bobby, 2025-09-19): This useEffect is used to make sure we reset the log subscription as well as
  // increment the refresh epoch so that we tell the iframe to make a request to that page.
  React.useEffect(() => {
    if (data?.wasRestarted) {
      resetLogSubscription();
      incrementRefreshEpoch?.();
      processLogMessage("🔄 Dev server restarted - refreshing iframe");
    } else {
      processLogMessage("Dev server already running");
    }
  }, [data, resetLogSubscription, processLogMessage, incrementRefreshEpoch]);

  const restartDevServer = React.useCallback(async () => {
    if (!projectId) {
      return;
    }
    await codeExecutionClient.daytona.restartDevServer.mutate({
      projectId,
    });
    resetLogSubscription();
  }, [resetLogSubscription, projectId]);

  return {
    error,
    url: data?.url,
    isLoading: false,
    restartDevServer,
  };
}
